import { useMemo } from 'react'

import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
} from 'react-hook-form'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRequiredIndicator,
  FieldRoot,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
} from '@enechain/ecloud-designsystem'

import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import {
  type BusinessMasterRow,
  convertListBusinessMastersResponse,
  useListBusinessMasters,
} from '~/hooks/businessMaster/useListBusinessMasters'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { licenseTypeLabel } from '~/models/licenseType'
import { isActiveOn } from '~/models/validPeriod'
import { SubmitterBgMemberFields } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/components/SubmitterBgMemberFields'
import type { SubmitterMasterFormInput } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/models/schema'
import { ExpiredMasterAlert } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredMasterAlert'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { TopLevelValidPeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/TopLevelValidPeriodFields'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import {
  resolveAsOfDate,
  resolveExpiryFallbackLabel,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type Props = {
  control: Control<SubmitterMasterFormInput>
  errors: FieldErrors<SubmitterMasterFormInput>
  setValue: UseFormSetValue<SubmitterMasterFormInput>
  /**
   * true の場合、 submitterCode / areaCode / licenseType を readonly で表示する (edit 時)。
   * AC-006 準拠: これらのフィールドは登録後に変更不可。
   */
  isEdit?: boolean
}

// 計画提出者マスタの BG 区分は発電 / DR のみ選択可能。
// 小売・自己託送・送配電は計画提出者として選択不可 (proto enum 値自体は存続。
// 既存データにこれらがあれば一覧はそのまま表示し、編集時は readonly)。
const LICENSE_TYPE_OPTIONS: LicenseType[] = [
  LicenseType.GENERATION,
  LicenseType.DR,
]

/**
 * 計画提出者マスタの Create / Edit 共通 form fields コンポーネント。
 *
 * isEdit={true} のとき submitterCode / areaCode / licenseType を readonly にする (AC-006)。
 * members 編集 UI は SubmitterBgMemberFields に委譲。
 */
export const SubmitterMasterFormFields = ({
  control,
  errors,
  setValue,
  isEdit = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { data: businessMastersData } = useListBusinessMasters()
  const businessMasters: BusinessMasterRow[] = useMemo(
    () =>
      businessMastersData !== undefined
        ? convertListBusinessMastersResponse(businessMastersData)
        : [],
    [businessMastersData],
  )

  // select の選択肢 (DS `Select` は `{ value: string, label }` を取る)。
  // ラベルは i18next 依存 (= 言語切替で変わる) なので render ごとに組み立てる
  // (native 時代に JSX 内で `.map` していたのと同じ扱い)。
  const licenseTypeOptions = LICENSE_TYPE_OPTIONS.map((lt) => ({
    value: String(lt),
    label: licenseTypeLabel(lt),
  }))
  const areaCodeOptions = AREA_CODE_OPTIONS.map((code) => ({
    value: String(code),
    label: convertAreaLabel(code),
  }))
  // AC-MOI-023: 事業者の候補は、 計画提出者の適用開始日時点で有効な事業者に絞る。
  const today = useTodayJst()
  const validFrom = useWatch({ control, name: 'validFrom' })
  const targetDate = resolveAsOfDate(undefined, validFrom, today)
  const businessMasterOptions = useMemo(
    () =>
      businessMasters
        .filter((bm) => isActiveOn(bm.validFrom, bm.validTo, targetDate))
        .map((bm) => ({
          value: bm.id,
          label: bm.businessName,
        })),
    [businessMasters, targetDate],
  )
  // 候補外になった選択済みの事業者は外さず、 適用前・終了の表示で残す (AC-MOI-023)。
  const businessMasterFallbackLabel = (id: string): string | undefined =>
    resolveExpiryFallbackLabel(
      businessMastersData === undefined ? undefined : businessMasters,
      id,
      targetDate,
      {
        idOf: (bm) => bm.id,
        labelOf: (bm) => bm.businessName,
        validTo: (bm) => bm.validTo,
        deletedLabel: t('page:adminOcctoMaster.common.masterFallback.deleted'),
        expiredSuffix: t(
          'page:adminOcctoMaster.common.masterFallback.expiredSuffix',
        ),
        future: {
          validFrom: (bm) => bm.validFrom,
          suffix: t('page:adminOcctoMaster.common.masterFallback.futureSuffix'),
        },
      },
    )

  // isEdit のとき licenseType / areaCode は readonly Input で表示する
  // (select を出さない) ため、 表示用に値を購読する。
  const licenseType = useWatch({ control, name: 'licenseType' })
  const areaCode = useWatch({ control, name: 'areaCode' })

  return (
    <Stack gap={4}>
      {/* 適用期間が終了している既存レコードを編集しているときの案内帯。
          新規作成では出さない (「終了している既存データ」を指す文言のため)。 */}
      {isEdit && (
        <ExpiredMasterAlert
          control={control as unknown as Control<FieldValues>}
          idPrefix="submitterMaster"
        />
      )}

      {/* submitterCode + licenseType + areaCode: 3カラム (design: 提出者コード/BG区分/エリア) */}
      <SimpleGrid gap={3} columns={3}>
        {/* submitterCode */}
        <Controller
          control={control}
          name="submitterCode"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={errors.submitterCode !== undefined}
              required
            >
              <FieldLabel htmlFor="submitterMaster-submitterCode">
                {t('page:adminOcctoMaster.submitterMaster.form.submitterCode')}
                <FieldRequiredIndicator />
              </FieldLabel>
              <ImmutableInput
                {...field}
                id="submitterMaster-submitterCode"
                maxLength={5}
                placeholder={t(
                  'page:adminOcctoMaster.submitterMaster.form.submitterCodePlaceholder',
                )}
                readOnly={isEdit}
              />
              {errors.submitterCode !== undefined && (
                <FieldErrorText>{errors.submitterCode.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />

        {/* licenseType。 単一 select の正は DS `Select`
            (`~/components/common/FormSelect` の JSDoc 参照)。
            native 時代の先頭 option (value=UNSPECIFIED の「選択してください」) は
            placeholder へ移す (= 未選択は値なしで表現し、 選択肢には並べない)。
            UNSPECIFIED は schema が reject する値なので、 候補から外れても
            保存可能な入力の集合は変わらない。 */}
        <FieldRoot
          alignItems="stretch"
          invalid={errors.licenseType !== undefined}
          required
        >
          {/* readonly Input のときだけ明示 id で紐付ける。 DS `Select` は
              FieldRoot context の id を自動で使うため htmlFor を渡さない
              (渡すと存在しない id を指す label になる)。 */}
          <FieldLabel
            htmlFor={isEdit ? 'submitterMaster-licenseType' : undefined}
          >
            {t('page:adminOcctoMaster.submitterMaster.form.licenseType')}
            <FieldRequiredIndicator />
          </FieldLabel>
          {isEdit ? (
            <ImmutableInput
              id="submitterMaster-licenseType"
              readOnly
              value={licenseTypeLabel(licenseType)}
            />
          ) : (
            <FormSelect
              aria-label={t(
                'page:adminOcctoMaster.submitterMaster.form.licenseType',
              )}
              control={control}
              name="licenseType"
              numeric
              options={licenseTypeOptions}
              placeholder={t(
                'page:adminOcctoMaster.submitterMaster.form.licenseTypeUnselected',
              )}
            />
          )}
          {errors.licenseType !== undefined && (
            <FieldErrorText>{errors.licenseType.message}</FieldErrorText>
          )}
        </FieldRoot>

        {/* areaCode (先頭の UNSPECIFIED option → placeholder。 理由は licenseType 側と同じ) */}
        <FieldRoot
          alignItems="stretch"
          invalid={errors.areaCode !== undefined}
          required
        >
          <FieldLabel htmlFor={isEdit ? 'submitterMaster-areaCode' : undefined}>
            {t('page:adminOcctoMaster.submitterMaster.form.areaCode')}
            <FieldRequiredIndicator />
          </FieldLabel>
          {isEdit ? (
            <ImmutableInput
              id="submitterMaster-areaCode"
              readOnly
              value={convertAreaLabel(areaCode)}
            />
          ) : (
            <FormSelect
              aria-label={t(
                'page:adminOcctoMaster.submitterMaster.form.areaCode',
              )}
              control={control}
              name="areaCode"
              numeric
              options={areaCodeOptions}
              placeholder={t(
                'page:adminOcctoMaster.submitterMaster.form.areaCodeUnselected',
              )}
            />
          )}
          {errors.areaCode !== undefined && (
            <FieldErrorText>{errors.areaCode.message}</FieldErrorText>
          )}
        </FieldRoot>
      </SimpleGrid>

      {/* submitterName (全幅、design: 全幅 1 行)。 design 側コメント (2026-06-30 UX指示):
          「事業者名+コードが長く 2fr1fr グリッドの 1fr 側では見切れたため全幅 1 行に」
          — impl も同じく全幅にする (旧: 提出者名 + 代表事業者 の 2 列)。 */}
      <Controller
        control={control}
        name="submitterName"
        render={({ field }) => (
          <FieldRoot
            alignItems="stretch"
            invalid={errors.submitterName !== undefined}
            required
          >
            <FieldLabel htmlFor="submitterMaster-submitterName">
              {t('page:adminOcctoMaster.submitterMaster.form.submitterName')}
              <FieldRequiredIndicator />
            </FieldLabel>
            <Input {...field} id="submitterMaster-submitterName" />
            {errors.submitterName !== undefined && (
              <FieldErrorText>{errors.submitterName.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />

      {/* businessMasterId (代表事業者、design: 全幅 1 行) */}
      <FieldRoot
        alignItems="stretch"
        invalid={errors.businessMasterId !== undefined}
        required
      >
        <FieldLabel>
          {t('page:adminOcctoMaster.submitterMaster.form.businessMaster')}
          <FieldRequiredIndicator />
        </FieldLabel>
        <FormSelect
          aria-label={t(
            'page:adminOcctoMaster.submitterMaster.form.businessMaster',
          )}
          control={control}
          fallbackLabel={businessMasterFallbackLabel}
          name="businessMasterId"
          options={businessMasterOptions}
          placeholder={t(
            'page:adminOcctoMaster.submitterMaster.form.businessMasterUnselected',
          )}
        />
        {errors.businessMasterId !== undefined && (
          <FieldErrorText>{errors.businessMasterId.message}</FieldErrorText>
        )}
      </FieldRoot>

      {/* note (任意、design 順: 代表事業者の直後、所属BGの前)。
          上限は submitter.md AC-OMS-011 (100 文字)。 */}
      <Controller
        control={control}
        name="note"
        render={({ field }) => (
          <FieldRoot invalid={errors.note !== undefined}>
            <FieldLabel>
              {t('page:adminOcctoMaster.submitterMaster.form.note')}
            </FieldLabel>
            <Textarea {...field} maxLength={100} value={field.value ?? ''} />
            {errors.note !== undefined && (
              <FieldErrorText>{errors.note.message}</FieldErrorText>
            )}
            <Text
              as="span"
              textStyle="captionNormal"
              marginTop={1}
              color="Text.midEmphasis"
            >
              {t('page:adminOcctoMaster.submitterMaster.form.noteHint')}
            </Text>
          </FieldRoot>
        )}
      />

      {/* members (所属 BG 動的リスト、 design 順: 備考の後、 適用期間の前) */}
      <SubmitterBgMemberFields control={control} setValue={setValue} />

      {/* validFrom + validTo (design 順: 末尾、 所属BGの後) — 共通 TopLevelValidPeriodFields */}
      <TopLevelValidPeriodFields
        control={control as unknown as Control<FieldValues>}
        idPrefix="submitterMaster"
        validFromError={errors.validFrom?.message}
        validToError={errors.validTo?.message}
      />
    </Stack>
  )
}
